import React, { useState, useEffect } from 'react';
import { useNPC } from '../../context/NPCContext';
import { transliterateGreekToLatin } from '../../utils/formatters';
import './FileImporter.css';

const FileImporter = () => {
  
  const { 
    setNpcNames,  
    setDialogues, 
    setButtons, 
    setCommands 
  } = useNPC();
  
  // Loading and progress states
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importStatus, setImportStatus] = useState('');
  const [statusClass, setStatusClass] = useState('');
  const [totalFiles, setTotalFiles] = useState(0);
  const [processedFiles, setProcessedFiles] = useState(0);
  
  // Preview states
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState({
    npcs: [],
    dialoguesCount: {},
    buttonsCount: {},
    commandsCount: {}
  });
  const [selectedNpcs, setSelectedNpcs] = useState([]);

  // Reset status message after a delay
  useEffect(() => {
    let timer;
    if (importStatus && !isLoading && !showPreview) {
      timer = setTimeout(() => {
        setImportStatus('');
        setStatusClass('');
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [importStatus, isLoading, showPreview]);

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    
    // Start loading
    setIsLoading(true);
    setProgress(0);
    setProcessedFiles(0);
    setTotalFiles(files.length);
    setImportStatus('Reading files...');
    setStatusClass('');
    setShowPreview(false);
    
    // First process all .lang files (to get translations)
    const langFiles = files.filter(file => file.name.endsWith('.lang'));
    const jsonFiles = files.filter(file => file.name.endsWith('.json'));
    
    // Store all translations in a global object
    let globalTranslations = {};
    
    try {
      setImportStatus(`Processing ${langFiles.length} language files...`);
      console.log('=== sssssssFileImporter component mounted ===');
      // Process language files sequentially
      for (let i = 0; i < langFiles.length; i++) {
        const file = langFiles[i];
        const content = await readFileContent(file);
        const translations = parseLangFile(content);
        
        // Merge with global translations
        globalTranslations = { ...globalTranslations, ...translations };
        
        // Update progress
        setProcessedFiles(prev => prev + 1);
        setProgress(Math.round((i + 1) / (langFiles.length + jsonFiles.length) * 100));
      }
      
      // Store translations in localStorage
      localStorage.setItem('translations', JSON.stringify(globalTranslations));
      
      // Store button translations separately for easier access
      const buttonTranslations = {};
      Object.entries(globalTranslations).forEach(([key, value]) => {
        if (key.startsWith('dialogue.button.')) {
          buttonTranslations[key] = value;
        }
      });
      localStorage.setItem('buttonTranslations', JSON.stringify(buttonTranslations));
      
      // Now process JSON files
      setImportStatus(`Processing ${jsonFiles.length} dialogue files...`);
      
      // Temporary storage for all imported data
      let importedNpcs = [];
      let importedDialogues = {};
      let importedButtons = {};
      let importedCommands = {};
      let dialoguesCount = {};
      let buttonsCount = {};
      let commandsCount = {};
      
      // Process JSON files sequentially
      for (let i = 0; i < jsonFiles.length; i++) {
        const file = jsonFiles[i];
        const content = await readFileContent(file);
        
        try {
          const jsonData = JSON.parse(content);
          // Get filename without extension to use as base npcTag
          const npcTag = file.name.replace('.json', '');
          
          // Get NPC name from translations or create one
          const npcNameKey = `dialogue.${npcTag}.name`;
          const npcName = globalTranslations[npcNameKey] || 
                         npcTag.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
          
          // Add NPC if not already present
          if (!importedNpcs.includes(npcName)) {
            importedNpcs.push(npcName);
          }

          // Initialize counters for this NPC
          dialoguesCount[npcName] = 0;
          buttonsCount[npcName] = 0;
          commandsCount[npcName] = 0;

          // Extract dialogues, buttons, and commands from the JSON
          if (jsonData && jsonData["minecraft:npc_dialogue"] && jsonData["minecraft:npc_dialogue"].scenes) {
            const scenes = jsonData["minecraft:npc_dialogue"].scenes;
            
            // Process each scene
            importedDialogues[npcName] = importedDialogues[npcName] || [];
            importedButtons[npcName] = importedButtons[npcName] || {};
            importedCommands[npcName] = importedCommands[npcName] || {};
            
            scenes.forEach(scene => {
              // IMPROVED: Extract dialogueIndex from scene_tag with better parsing
              // Handle different formats: "npcTag.index" or just "index"
              let dialogueIndex = 0;
              
              if (scene.scene_tag) {
                // Split by period and take the last part as the index
                const parts = scene.scene_tag.split('.');
                if (parts.length > 1) {
                  // Format is "something.number" - take the last part
                  dialogueIndex = parseInt(parts[parts.length - 1], 10);
                } else {
                  // Format is just "number"
                  dialogueIndex = parseInt(scene.scene_tag, 10);
                }
                
                // Default to 0 if parsing failed
                if (isNaN(dialogueIndex)) {
                  dialogueIndex = 0;
                }
              }
              
              // Find dialogue text using translations
              let dialogueText = '';
              
              // Try different ways to get dialogue text
              // 1. First check if there's a translation for this scene in the format dialogue.npcTag.index
              const dialogueKey = `dialogue.${npcTag}.${dialogueIndex}`;
              if (globalTranslations[dialogueKey]) {
                dialogueText = globalTranslations[dialogueKey];
              } 
              // 2. Check for other formats that might be used
              else if (scene.text && scene.text.rawtext && scene.text.rawtext.length > 0) {
                const rawtext = scene.text.rawtext[0];
                
                // Direct text
                if (rawtext.text) {
                  dialogueText = rawtext.text;
                } 
                // Translated text
                else if (rawtext.translate) {
                  // Try to get from translations, or use the key itself
                  dialogueText = globalTranslations[rawtext.translate] || rawtext.translate;
                  
                  // If it's a translation key but not found in our translations, try to parse it
                  if (!globalTranslations[rawtext.translate] && rawtext.translate.startsWith('dialogue.')) {
                    // Extract the dialogue index from the translation key
                    const keyParts = rawtext.translate.split('.');
                    if (keyParts.length >= 3) {
                      const extractedIndex = parseInt(keyParts[keyParts.length - 1], 10);
                      if (!isNaN(extractedIndex)) {
                        // Use the extracted index if we found one
                        dialogueIndex = extractedIndex;
                      }
                    }
                  }
                }
              }
              
              // Store dialogue if we found text
              if (dialogueText) {
                importedDialogues[npcName][dialogueIndex] = dialogueText;
                dialoguesCount[npcName]++;
              }
              
              // Process buttons
              if (scene.buttons && scene.buttons.length > 0) {
                // Store buttons and commands for this dialogue
                importedButtons[npcName][dialogueText] = [];
                importedCommands[npcName][dialogueText] = {};
                
                // Process each button
                scene.buttons.forEach(buttonObj => {
                  let buttonText = '';
                  
                  // Try to get button text from translations or raw text
                  if (buttonObj.name && buttonObj.name.rawtext && buttonObj.name.rawtext.length > 0) {
                    const rawtext = buttonObj.name.rawtext[0];
                    
                    if (rawtext.text) {
                      buttonText = rawtext.text;
                    } else if (rawtext.translate) {
                      buttonText = globalTranslations[rawtext.translate] || rawtext.translate;
                      
                      // If it's a button key but not in translations, try to extract button name
                      if (!globalTranslations[rawtext.translate] && rawtext.translate.startsWith('dialogue.button.')) {
                        // Convert from dialogue.button.some_button to Some Button
                        const buttonKey = rawtext.translate.replace('dialogue.button.', '');
                        buttonText = buttonKey.split('_')
                          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                          .join(' ');
                      }
                    }
                  }
                  
                  // Add button if we found text
                  if (buttonText) {
                    importedButtons[npcName][dialogueText].push(buttonText);
                    buttonsCount[npcName]++;
                    
                    // Store commands for this button
                    if (buttonObj.commands && Array.isArray(buttonObj.commands)) {
                      importedCommands[npcName][dialogueText][buttonText] = buttonObj.commands;
                      commandsCount[npcName] += buttonObj.commands.length;
                    } else {
                      importedCommands[npcName][dialogueText][buttonText] = [];
                    }
                  }
                });
              }
            });
          }
          
        } catch (error) {
          console.error(`Error processing JSON file ${file.name}:`, error);
        }
        
        // Update progress
        setProcessedFiles(prev => prev + langFiles.length + i + 1);
        setProgress(Math.round((langFiles.length + i + 1) / (langFiles.length + jsonFiles.length) * 100));
      }
      
      // Show preview if data was imported
      if (importedNpcs.length > 0) {
        setPreviewData({
          npcs: importedNpcs,
          dialoguesCount,
          buttonsCount,
          commandsCount,
          dialogues: importedDialogues,
          buttons: importedButtons,
          commands: importedCommands
        });
        setSelectedNpcs([...importedNpcs]);
        setShowPreview(true);
        setImportStatus(`Preview ready. ${importedNpcs.length} NPCs found.`);
      } else {
        setImportStatus('No valid data found in the imported files.');
        setStatusClass('error');
      }
      
    } catch (error) {
      console.error("Import error:", error);
      setImportStatus(`Import error: ${error.message}`);
      setStatusClass('error');
    } finally {
      setIsLoading(false);
    }
    
    // Clear the file input
    event.target.value = '';
  };
  
  // Confirm import
  const handleConfirmImport = () => {
    if (selectedNpcs.length === 0) {
      setImportStatus('Please select at least one NPC to import.');
      setStatusClass('error');
      return;
    }
    
    // Get data from preview
    const { dialogues: importedDialogues, buttons: importedButtons, commands: importedCommands } = previewData;
  
    // Update state with selected NPCs and their data
    setNpcNames(prevNpcNames => {
      // Add only selected NPCs that don't already exist
      const uniqueNewNpcs = selectedNpcs.filter(npc => !prevNpcNames.includes(npc));
      return [...prevNpcNames, ...uniqueNewNpcs];
    });
    
    setDialogues(prevDialogues => {
      // Merge with existing dialogues, only for selected NPCs
      const mergedDialogues = { ...prevDialogues };
      
      Object.entries(importedDialogues).forEach(([npc, dialogueArray]) => {
        if (selectedNpcs.includes(npc)) {
          // Important: Preserve the array structure for mcfunction generation
          // Make sure we're dealing with a proper array, not an object with numeric keys
          if (!Array.isArray(dialogueArray)) {
            // Convert object with numeric keys to array
            const maxIndex = Math.max(...Object.keys(dialogueArray).map(Number));
            const properArray = new Array(maxIndex + 1);
            
            // Fill array with values, preserving indices
            Object.entries(dialogueArray).forEach(([idx, dialogue]) => {
              properArray[Number(idx)] = dialogue;
            });
            
            dialogueArray = properArray;
          }
          
          // Create a fresh array or use existing one
          mergedDialogues[npc] = mergedDialogues[npc] || [];
          
          // Find the highest index in both arrays
          const currentMaxIndex = mergedDialogues[npc].length - 1;
          const newMaxIndex = dialogueArray.length - 1;
          const combinedMaxIndex = Math.max(currentMaxIndex, newMaxIndex);
          
          // Create a new array of proper length
          const combinedArray = new Array(combinedMaxIndex + 1);
          
          // Copy existing values
          for (let i = 0; i <= currentMaxIndex; i++) {
            combinedArray[i] = mergedDialogues[npc][i];
          }
          
          // Add new values, overriding existing ones if needed
          for (let i = 0; i <= newMaxIndex; i++) {
            if (dialogueArray[i] !== undefined) {
              combinedArray[i] = dialogueArray[i];
            }
          }
          
          // Update the dialogues with the combined array
          mergedDialogues[npc] = combinedArray;
        }
      });
      
      return mergedDialogues;
    });
    
    setButtons(prevButtons => {
      // Merge with existing buttons, only for selected NPCs
      const mergedButtons = { ...prevButtons };
      Object.entries(importedButtons).forEach(([npc, dialogueMap]) => {
        if (selectedNpcs.includes(npc)) {
          mergedButtons[npc] = mergedButtons[npc] || {};
          Object.entries(dialogueMap).forEach(([dialogue, buttonArray]) => {
            mergedButtons[npc][dialogue] = buttonArray;
          });
        }
      });
      return mergedButtons;
    });
    
    setCommands(prevCommands => {
      // Merge with existing commands, only for selected NPCs
      const mergedCommands = { ...prevCommands };
      Object.entries(importedCommands).forEach(([npc, dialogueMap]) => {
        if (selectedNpcs.includes(npc)) {
          mergedCommands[npc] = mergedCommands[npc] || {};
          Object.entries(dialogueMap).forEach(([dialogue, buttonMap]) => {
            mergedCommands[npc][dialogue] = mergedCommands[npc][dialogue] || {};
            Object.entries(buttonMap).forEach(([button, commandArray]) => {
              mergedCommands[npc][dialogue][button] = commandArray;
            });
          });
        }
      });
      return mergedCommands;
    });
    
    // Show success message and close preview
    setImportStatus(`Successfully imported ${selectedNpcs.length} NPCs with their data.`);
    setStatusClass('success');
    setShowPreview(false);
  };

  // Cancel import
  const handleCancelImport = () => {
    setShowPreview(false);
    setImportStatus('Import cancelled.');
    setStatusClass('');
  };

  // Toggle NPC selection in preview
  const toggleNpcSelection = (npc) => {
    setSelectedNpcs(prev => {
      if (prev.includes(npc)) {
        return prev.filter(n => n !== npc);
      } else {
        return [...prev, npc];
      }
    });
  };

  // Toggle all NPCs selection in preview
  const toggleAllNpcs = () => {
    if (selectedNpcs.length === previewData.npcs.length) {
      setSelectedNpcs([]);
    } else {
      setSelectedNpcs([...previewData.npcs]);
    }
  };

  // Helper function to read file content
  const readFileContent = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = e => resolve(e.target.result);
      reader.onerror = e => reject(e);
      reader.readAsText(file);
    });
  };

  // Enhanced helper function to parse .lang file
  const parseLangFile = (content) => {
    const lines = content.split('\n');
    let translations = {};

    lines.forEach(line => {
      if (!line.trim() || !line.includes('=')) return;
      
      const [key, ...valueParts] = line.split('=');
      // Handle values that might contain '=' characters
      const value = valueParts.join('=');
      
      // Check if the key contains Greek characters and needs transliteration for internal use
      // The original Greek text is preserved in the value
      let processedKey = key.trim();
      
      // For button keys, we need to specially handle them
      if (processedKey.startsWith('dialogue.button.')) {
        // Keep the prefix intact, but transliterate the button name part if needed
        const prefix = 'dialogue.button.';
        const buttonPart = processedKey.substring(prefix.length);
        
        // Check if the button part contains Greek characters
        const hasGreekChars = /[\u0370-\u03FF\u1F00-\u1FFF]/.test(buttonPart);
        
        if (hasGreekChars) {
          // Replace Greek with Latin equivalents for the key but keep original text in value
          const transliteratedButtonPart = transliterateGreekToLatin(buttonPart);
          processedKey = prefix + transliteratedButtonPart.toLowerCase().replace(/\s+/g, '_').replace(/\W/g, '');
        }
      }
      
      // Store with possibly transliterated key
      translations[processedKey] = value;
    });

    return translations;
  };

  return (
    <div className="file-importer">
      {!showPreview ? (
        <div className="import-form">
          <div className="import-header">
            <h3>Import Files</h3>
            <p className="help-text">
              Import .json and .lang files to load NPC dialogues.
            </p>
          </div>
          
          <div className="file-input-container">
            <input 
              type="file" 
              id="file-input" 
              onChange={handleFileChange}
              multiple
              accept=".json,.lang"
              disabled={isLoading}
            />
            <label htmlFor="file-input" className={isLoading ? 'disabled' : ''}>
              {isLoading ? 'Importing...' : 'Choose Files'}
            </label>
            <span className="file-format">.json, .lang</span>
          </div>
          
          {isLoading && (
            <div className="progress-container">
              <div className="progress-bar" style={{ width: `${progress}%` }}></div>
              <div className="progress-text">{processedFiles} / {totalFiles} files processed</div>
            </div>
          )}
          
          {importStatus && (
            <div className={`import-status ${statusClass}`}>
              {importStatus}
            </div>
          )}
        </div>
      ) : (
        <div className="import-preview">
          <h3>Import Preview</h3>
          <div className="preview-controls">
            <button 
              onClick={toggleAllNpcs}
              className="select-all-button"
            >
              {selectedNpcs.length === previewData.npcs.length ? 'Deselect All' : 'Select All'}
            </button>
            <div className="count-info">
              {selectedNpcs.length} of {previewData.npcs.length} NPCs selected
            </div>
          </div>
          
          <div className="npc-list">
            {previewData.npcs.map(npc => (
              <div 
                key={npc} 
                className={`npc-item ${selectedNpcs.includes(npc) ? 'selected' : ''}`}
                onClick={() => toggleNpcSelection(npc)}
              >
                <div className="npc-checkbox">
                  <input 
                    type="checkbox" 
                    checked={selectedNpcs.includes(npc)}
                    onChange={() => {}} // Handled by onClick on parent div
                  />
                </div>
                <div className="npc-info">
                  <div className="npc-name">{npc}</div>
                  <div className="npc-details">
                    <span>{previewData.dialoguesCount[npc]} dialogues</span>
                    <span>{previewData.buttonsCount[npc]} buttons</span>
                    <span>{previewData.commandsCount[npc]} commands</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="preview-actions">
            <button 
              onClick={handleCancelImport}
              className="cancel-button"
            >
              Cancel
            </button>
            <button 
              onClick={handleConfirmImport}
              className="confirm-button"
              disabled={selectedNpcs.length === 0}
            >
              Import Selected
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileImporter;